import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  ArrowRight, 
  FileText, 
  Eye, 
  Briefcase, 
  TrendingUp,
  CheckCircle2,
  Shield,
  BarChart3,
  Users,
  Building2,
  Lock,
  Layers,
  Coins,
  Scale,
  Wallet
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const ForInvestorsPage = () => {
  const navigate = useNavigate();
  const { user, openAuthModal } = useAuth();

  const keyPoints = [
    { 
      icon: Building2, 
      title: 'Own Real Estate Equity', 
      desc: 'When you invest through Flippex, you own actual equity in a real estate asset. This is not a derivative or debt instrument — you\'re a real owner.' 
    },
    { 
      icon: Coins, 
      title: 'Fractional Ownership', 
      desc: 'Access institutional-grade real estate projects with fractional investment. Own a piece of properties that would otherwise require significant capital.' 
    },
    { 
      icon: Eye, 
      title: 'Transparent Governance', 
      desc: 'Understand who is behind each project. Review sponsor track records, participate in governance, and receive regular reporting.' 
    },
    { 
      icon: TrendingUp, 
      title: 'Collect Returns', 
      desc: 'Receive your share of rental income distributions and benefit from property appreciation. Real estate returns, fractionally.' 
    },
  ];

  const spvBenefits = [
    { icon: Shield, title: 'Asset Protection', desc: 'Each property is ring-fenced in its own legal entity, protecting your investment.' },
    { icon: Scale, title: 'Clear Legal Rights', desc: 'Your equity ownership is recorded on the SPV cap table with full legal standing.' },
    { icon: FileText, title: 'Standardized Structure', desc: 'Consistent legal frameworks across all projects for simpler due diligence.' },
    { icon: Lock, title: 'Regulatory Compliance', desc: 'SPV structures ensure compliance with securities and real estate regulations.' },
  ];

  const howItWorks = [
    { step: 1, title: 'Sign Up', desc: 'Create your account and complete investor verification (KYC).' },
    { step: 2, title: 'Browse & Research', desc: 'Explore projects with standardized disclosures and project details.' },
    { step: 3, title: 'Invest & Own', desc: 'Subscribe to projects and receive tokenized equity in the SPV.' },
    { step: 4, title: 'Track & Earn', desc: 'Monitor your portfolio, receive distributions, and participate in governance.' },
  ];

  const benefits = [
    { icon: Shield, text: 'Compliance-first platform' },
    { icon: FileText, text: 'Standardized documentation' },
    { icon: Users, text: 'Direct sponsor communication' },
    { icon: BarChart3, text: 'Unified portfolio dashboard' },
  ];

  const handleGetStarted = () => {
    if (user) {
      navigate('/projects');
    } else {
      openAuthModal('register');
    }
  };

  return (
    <div className="min-h-screen" data-testid="for-investors-page">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-20 pb-16 md:pt-28 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div 
            className="absolute inset-0" 
            style={{ 
              background: 'linear-gradient(135deg, #090C4F 0%, #1a1f5e 50%, #D73986 100%)', 
              opacity: 0.25 
            }}
          />
        </div>

        <div className="container mx-auto px-6 md:px-12 lg:px-24 xl:px-32 relative z-10">
          <div className="max-w-4xl">
            <span className="inline-block px-4 py-1.5 rounded-full text-sm font-medium bg-accent/20 text-accent mb-6">
              For Investors
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-heading font-bold tracking-tight mb-6 leading-[1.1]" data-testid="investors-hero-heading">
              Own real estate.
              <br />
              <span className="text-gradient">Collect real returns.</span>
            </h1>
            <p className="text-base md:text-lg text-foreground-muted mb-8 max-w-2xl">
              Invest in fractional equity ownership of student housing and homeownership properties. Each asset is structured as an SPV — you own real equity, not just exposure.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                className="bg-primary hover:bg-primary-hover rounded-full px-8"
                onClick={() => navigate('/projects')}
                data-testid="investors-explore-btn"
              >
                Explore Projects <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="rounded-full px-8"
                onClick={handleGetStarted}
              >
                Create Account
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Key Points Section */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 xl:px-32">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Why Invest Through Flippex
            </h2>
            <p className="text-base text-foreground-muted max-w-2xl mx-auto">
              True equity ownership in carefully structured real estate projects.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {keyPoints.map((point, idx) => {
              const Icon = point.icon;
              return (
                <div 
                  key={idx} 
                  className="p-6 rounded-2xl bg-background-paper border border-white/5 hover:border-primary/30 transition-colors"
                >
                  <Icon className="h-10 w-10 text-primary mb-4" />
                  <h3 className="text-xl font-semibold mb-3">{point.title}</h3>
                  <p className="text-sm text-foreground-muted">{point.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How Ownership Works - SPV & Tokenization */}
      <section className="py-16 md:py-20 bg-background-paper/50">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 xl:px-32">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full text-sm font-medium bg-primary/20 text-primary mb-4">
              The Technology
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              How Ownership Works
            </h2>
            <p className="text-base text-foreground-muted max-w-3xl mx-auto">
              Each real estate asset on Flippex is incorporated into a Special Purpose Vehicle (SPV). When you invest, you receive tokenized equity representing your ownership stake in that SPV.
            </p>
          </div>

          {/* Visual Diagram */}
          <div className="max-w-4xl mx-auto mb-12">
            <Card className="glass p-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                {/* Property */}
                <div className="text-center">
                  <div className="h-20 w-20 rounded-2xl bg-primary/20 flex items-center justify-center mx-auto mb-3">
                    <Building2 className="h-10 w-10 text-primary" />
                  </div>
                  <h4 className="font-semibold mb-1">Property Asset</h4>
                  <p className="text-xs text-foreground-muted">Real estate</p>
                </div>

                <ArrowRight className="h-6 w-6 text-foreground-muted rotate-90 md:rotate-0" />

                {/* SPV */}
                <div className="text-center">
                  <div className="h-20 w-20 rounded-2xl bg-accent/20 flex items-center justify-center mx-auto mb-3">
                    <Layers className="h-10 w-10 text-accent" />
                  </div>
                  <h4 className="font-semibold mb-1">SPV Structure</h4>
                  <p className="text-xs text-foreground-muted">Legal entity holds asset</p>
                </div>

                <ArrowRight className="h-6 w-6 text-foreground-muted rotate-90 md:rotate-0" />

                {/* Tokens */}
                <div className="text-center">
                  <div className="h-20 w-20 rounded-2xl bg-primary/20 flex items-center justify-center mx-auto mb-3">
                    <Coins className="h-10 w-10 text-primary" />
                  </div>
                  <h4 className="font-semibold mb-1">Tokenized Equity</h4>
                  <p className="text-xs text-foreground-muted">Digital ownership proof</p>
                </div>

                <ArrowRight className="h-6 w-6 text-foreground-muted rotate-90 md:rotate-0" />

                {/* Investor */}
                <div className="text-center">
                  <div className="h-20 w-20 rounded-2xl bg-accent/20 flex items-center justify-center mx-auto mb-3">
                    <Wallet className="h-10 w-10 text-accent" />
                  </div>
                  <h4 className="font-semibold mb-1">You Own Equity</h4>
                  <p className="text-xs text-foreground-muted">Legal shareholder</p>
                </div>
              </div>
            </Card>
          </div>

          {/* SPV Benefits Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {spvBenefits.map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <Card key={idx} className="glass p-5 text-center">
                  <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
                    <Icon className="h-6 w-6 text-primary" />
                  </div>
                  <h4 className="font-semibold mb-2">{benefit.title}</h4>
                  <p className="text-xs text-foreground-muted">{benefit.desc}</p>
                </Card>
              );
            })}
          </div>

          {/* Additional Context */}
          <div className="max-w-3xl mx-auto mt-12 text-center">
            <p className="text-base text-foreground-muted leading-relaxed">
              Tokenization is the process of representing your equity ownership digitally on a blockchain. This provides transparent record-keeping, enables potential future liquidity, and ensures your ownership rights are immutably recorded. Each token you hold represents a proportional share of the SPV and, by extension, the underlying property.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works for Investors */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 xl:px-32">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Getting Started
            </h2>
            <p className="text-base text-foreground-muted max-w-2xl mx-auto">
              From signup to portfolio management in four simple steps.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {howItWorks.map((step, idx) => (
              <div 
                key={idx} 
                className="relative p-6 rounded-2xl bg-background-paper border border-white/5 text-center"
              >
                <div className="h-12 w-12 rounded-full bg-primary text-white text-lg font-bold flex items-center justify-center mx-auto mb-4">
                  {step.step}
                </div>
                <h3 className="text-lg font-semibold mb-2">{step.title}</h3>
                <p className="text-sm text-foreground-muted">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Strip */}
      <section className="py-16 md:py-20 bg-background-paper/30">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 xl:px-32">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {benefits.map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <div key={idx} className="text-center">
                  <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                    <Icon className="h-7 w-7 text-primary" />
                  </div>
                  <p className="text-sm font-medium text-foreground-muted">{benefit.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-primary/10 via-background to-accent/10">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 xl:px-32">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Start Owning Real Estate Today
            </h2>
            <p className="text-base text-foreground-muted mb-8">
              Browse available projects and create your account to access fractional real estate equity ownership.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-primary hover:bg-primary-hover rounded-full px-8"
                onClick={() => navigate('/projects')}
              >
                View Projects <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="rounded-full px-8"
                onClick={handleGetStarted}
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ForInvestorsPage;
